import { useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/Common";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { GraduationCap, Rocket, Save } from "lucide-react";
import { DeleteAccountCard } from "@/components/account/DeleteAccountCard";
import { CountrySelect, TargetCountriesPicker } from "@/components/forms/CountryPicker";

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    persona: user?.persona || "undergraduate",
    field_of_study: user?.profile?.field_of_study || "",
    target_country: user?.profile?.target_country || "",
    country: user?.profile?.country || "",
    academic_level: user?.profile?.academic_level || "",
    start_year: user?.profile?.start_year || "",
    bio: user?.profile?.bio || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", {
        name: form.name,
        persona: form.persona,
        profile: {
          ...user?.profile,
          persona: form.persona,
          field_of_study: form.field_of_study,
          target_country: form.target_country,
          country: form.country,
          academic_level: form.academic_level,
          start_year: form.start_year,
          bio: form.bio,
        },
      });
      updateUser(data);
      toast.success("Profile updated — your advisor now knows more about you!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-5 py-8">
        <PageHeader title="Profile" subtitle="Keep this up to date — it personalizes every AI answer and generated document." />
        <form onSubmit={save} className="bg-white rounded-3xl border border-border shadow-card p-6 space-y-5" data-testid="profile-form">
          <div className="flex items-center gap-3 pb-4 border-b border-border">
            <div className="h-12 w-12 rounded-full bg-orange flex items-center justify-center text-white font-display font-black italic text-lg">{user?.name?.[0]}</div>
            <div><div className="font-bold text-navy">{user?.email}</div><div className="text-xs uppercase tracking-wide text-muted-foreground">{user?.role}</div></div>
          </div>

          <div>
            <Label className="mb-2 block">Profile type</Label>
            <div className="grid grid-cols-2 gap-3">
              {[{ k: "undergraduate", i: GraduationCap, l: "Undergraduate" }, { k: "postgraduate", i: Rocket, l: "Postgraduate" }].map((p) => {
                const Icon = p.i; const active = form.persona === p.k;
                return (
                  <button type="button" key={p.k} data-testid={`profile-persona-${p.k}`} onClick={() => set("persona", p.k)} className={`p-4 rounded-2xl border flex items-center gap-3 transition-all ${active ? "border-orange bg-orange/5 ring-1 ring-orange" : "border-border"}`}>
                    <Icon className={`h-5 w-5 ${active ? "text-orange" : "text-navy"}`} /><span className="font-bold text-navy">{p.l}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div><Label htmlFor="name">Full name</Label><Input id="name" data-testid="profile-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Field of interest</Label><Input value={form.field_of_study} onChange={(e) => set("field_of_study", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label>Country of residence</Label><CountrySelect testId="profile-residence-country" value={form.country} onChange={(v) => set("country", v)} /></div>
            <div className="col-span-2"><Label>Target countries <span className="font-normal text-muted-foreground">— choose several or none</span></Label><TargetCountriesPicker testId="profile-target-countries" value={form.target_country} onChange={(v) => set("target_country", v)} /></div>
            <div><Label>Academic level</Label><Input value={form.academic_level} onChange={(e) => set("academic_level", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label>Start year</Label><Input value={form.start_year} onChange={(e) => set("start_year", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
          </div>
          <div><Label htmlFor="bio">About you</Label><Textarea id="bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={3} placeholder="A few lines about your goals & background" className="mt-1.5 rounded-xl" /></div>

          <Button type="submit" data-testid="profile-save" disabled={saving} className="w-full h-12 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide"><Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save profile"}</Button>
        </form>
        <DeleteAccountCard />
      </div>
    </AppLayout>
  );
}
